import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useAction } from 'wasp/client/operations';
import { getSectionById, getSections, updateSection } from 'wasp/client/operations';
import { useAuth } from 'wasp/client/auth';
import {
  Layers,
  Save,
  AlertCircle,
  HelpCircle
} from 'lucide-react';
import {
  IndividualEditPageLayout,
  SidebarSection,
  ActionButtons,
  ActionButton,
  ActionButtonSecondary
} from '../../components/layout';
import { toast } from '../../hooks/use-toast';

// Tooltip component (reusable)
const Tooltip = ({ children, content }: { children: React.ReactNode; content: string }) => {
  const [isVisible, setIsVisible] = useState(false);

  return (
    <div className="relative inline-block">
      <div
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        className="cursor-help"
      >
        {children}
      </div>
      {isVisible && (
        <div className="absolute z-10 px-3 py-2 text-sm text-white bg-gray-900 rounded-lg shadow-lg bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64">
          {content}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
        </div>
      )}
    </div>
  );
};

interface SectionFormData {
  section_key: string;
  parent_section_id: string;
  order: number;
  isBase: boolean;
  isTypeSpecific: boolean;
  archived: boolean;
}

interface FormErrors {
  section_key?: string;
  submit?: string;
}

export default function EditSectionPage() {
  const { sectionId } = useParams<{ sectionId: string }>();
  const { data: user } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});

  // Fetch data
  const { data: section, isLoading: isSectionLoading, error: sectionError } = useQuery(
    getSectionById,
    { id: sectionId! }
  );
  const { data: sections = [] } = useQuery(getSections);

  const updateSectionFn = useAction(updateSection);

  const [formData, setFormData] = useState<SectionFormData>({
    section_key: '',
    parent_section_id: '',
    order: 0,
    isBase: false,
    isTypeSpecific: false,
    archived: false
  });

  // Populate form when section data is loaded
  useEffect(() => {
    if (section) {
      setFormData({
        section_key: section.section_key || '',
        parent_section_id: section.parent_section_id || '',
        order: section.order || 0,
        isBase: section.isBase || false,
        isTypeSpecific: section.isTypeSpecific || false,
        archived: section.archived || false
      });
    }
  }, [section?.id, section?.section_key, section?.parent_section_id, section?.order, section?.isBase, section?.isTypeSpecific, section?.archived]);

  const handleChange = (field: keyof SectionFormData, value: string | boolean | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  // Filter out the current section and its descendants to avoid circular references
  const getDescendantIds = (parentId: string, allSections: any[]): string[] => {
    const children = allSections.filter((s: any) => s.parent_section_id === parentId);
    let ids: string[] = [];
    for (const child of children) {
      ids.push(child.id);
      ids = [...ids, ...getDescendantIds(child.id, allSections)];
    }
    return ids;
  };

  const descendantIds = sectionId ? getDescendantIds(sectionId, sections as any[]) : [];
  const availableParentSections = (sections as any[]).filter(
    (s: any) => s.id !== sectionId && !descendantIds.includes(s.id)
  );

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.section_key.trim()) {
      newErrors.section_key = 'Section key is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      await updateSectionFn({
        id: sectionId!,
        section_key: formData.section_key,
        parent_section_id: formData.parent_section_id || undefined,
        order: formData.order,
        isBase: formData.isBase,
        isTypeSpecific: formData.isTypeSpecific,
        archived: formData.archived
      });

      toast({
        title: "Success",
        description: "Section updated successfully"
      });

      navigate(`/admin/sections/${sectionId}`);
    } catch (error: any) {
      console.error('Error updating section:', error);
      setErrors({ submit: error.message || 'Failed to update section. Please try again.' });
      toast({
        title: "Error",
        description: error.message || 'Failed to update section',
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(`/admin/sections/${sectionId}`);
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Authentication Required</h2>
          <p className="text-gray-600">You must be logged in to edit sections.</p>
        </div>
      </div>
    );
  }

  if (isSectionLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading section...</p>
        </div>
      </div>
    );
  }

  if (sectionError || !section) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Layers className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Section Not Found</h3>
          <p className="text-gray-600 mb-4">The section you're looking for doesn't exist or has been removed.</p>
          <button
            onClick={() => navigate('/admin/sections')}
            className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Back to Sections
          </button>
        </div>
      </div>
    );
  }

  const sidebarContent = (
    <>
      <SidebarSection isFirst>
        <ActionButtons>
          <ActionButton
            type="submit"
            form="section-form"
            disabled={isLoading}
            icon={Save}
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Updating...
              </>
            ) : (
              'Update'
            )}
          </ActionButton>

          <ActionButtonSecondary
            onClick={handleCancel}
            disabled={isLoading}
          >
            Cancel
          </ActionButtonSecondary>
        </ActionButtons>
      </SidebarSection>

      <SidebarSection title="Settings">
        <div className="space-y-4">
          <div className="space-y-3">
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.isBase}
                onChange={(e) => {
                  handleChange('isBase', e.target.checked);
                  if (e.target.checked) handleChange('isTypeSpecific', false);
                }}
                className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500 rounded"
              />
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-700">Base Section</span>
                <Tooltip content="Base sections are available to all organization types">
                  <HelpCircle className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                </Tooltip>
              </div>
            </label>

            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.isTypeSpecific}
                onChange={(e) => {
                  handleChange('isTypeSpecific', e.target.checked);
                  if (e.target.checked) handleChange('isBase', false);
                }}
                className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500 rounded"
              />
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-700">Type-Specific</span>
                <Tooltip content="Type-specific sections are only available to certain organization types">
                  <HelpCircle className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                </Tooltip>
              </div>
            </label>

            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.archived}
                onChange={(e) => handleChange('archived', e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500 rounded"
              />
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-700">Archived</span>
                <Tooltip content="Archived sections are hidden from normal views">
                  <HelpCircle className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                </Tooltip>
              </div>
            </label>
          </div>
        </div>
      </SidebarSection>

      <SidebarSection title="Help">
        <div className="space-y-2 text-sm text-gray-600">
          <p>• Section key must be unique</p>
          <p>• Avoid circular parent references</p>
          <p>• Base sections appear for all org types</p>
          <p>• Type-specific sections are customizable per org type</p>
        </div>
      </SidebarSection>
    </>
  );

  return (
    <IndividualEditPageLayout sidebarContent={sidebarContent} layoutVariant="taxonomy" authUser={user}>
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-4">
          <Layers className="w-8 h-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Edit Section</h1>
            <p className="text-gray-600">Editing: <span className="font-mono">{section.section_key}</span></p>
          </div>
        </div>
      </div>

      {/* Form */}
      <form id="section-form" onSubmit={handleSubmit}>
        {/* Primary Input Fields */}
        <div className="space-y-6 mb-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Section Key *</h3>
              <input
                type="text"
                value={formData.section_key}
                onChange={(e) => handleChange('section_key', e.target.value)}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors ${
                  errors.section_key ? 'border-destructive bg-destructive/10' : 'border-input'
                }`}
                placeholder="e.g., main_content"
                required
              />
              {errors.section_key && (
                <p className="mt-1 text-sm text-destructive flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.section_key}
                </p>
              )}
              <p className="text-sm text-gray-500 mt-2">Unique identifier for this section (snake_case recommended)</p>
            </div>

            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Order</h3>
              <input
                type="number"
                value={formData.order}
                onChange={(e) => handleChange('order', parseInt(e.target.value) || 0)}
                className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                placeholder="0"
              />
              <p className="text-sm text-gray-500 mt-2">Display order (lower numbers appear first)</p>
            </div>
          </div>

          {/* Parent Section */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Parent Section</h3>
            <select
              value={formData.parent_section_id}
              onChange={(e) => handleChange('parent_section_id', e.target.value)}
              className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
            >
              <option value="">None (Top-level section)</option>
              {availableParentSections.map((s: any) => (
                <option key={s.id} value={s.id}>{s.section_key}</option>
              ))}
            </select>
            <p className="text-sm text-gray-500 mt-2">Optional: Select a parent to create a nested section</p>
          </div>
        </div>

        {/* Current Information */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-gray-700 mb-3">Current Information</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Status</span>
                <span className="text-gray-900">{section.archived ? 'Archived' : 'Active'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Type</span>
                <span className="text-gray-900">
                  {section.isBase ? 'Base' : section.isTypeSpecific ? 'Type-Specific' : 'Standard'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Sub-Sections</span>
                <span className="text-gray-900">{section._count?.subSections || 0}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Error message */}
        {errors.submit && (
          <div className="mt-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {errors.submit}
          </div>
        )}
      </form>
    </IndividualEditPageLayout>
  );
}



